/**
 * NCC: the over-18 confirmation for alcohol orders (snippets/ncc-age-gate.liquid).
 *
 * The checkbox is a required field of the cart form named as a cart attribute, so the browser alone stops the
 * theme's Checkout button until it is ticked and Shopify records the tick on the order. This script adds two things:
 * the tick is saved to the cart the moment it is made (`cart/update.js`), so Horizon's drawer keeps it when it
 * re-renders after a quantity change and a checkout reached another way still carries it; and an early press of
 * Checkout shows the theme's own hint under the box instead of the browser's validation bubble.
 *
 * Loaded once per page (cart-drawer.liquid, _cart-summary.liquid), never inside the markup Horizon morphs: a script
 * tag that arrives through a section re-render never executes.
 */

class AgeGate extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {Promise<void>} */
  #queue = Promise.resolve();

  connectedCallback() {
    this.#input = this.querySelector('input[type="checkbox"]');
    if (!this.#input) return;
    this.#input.addEventListener('change', this.#onChange);
    // 'invalid' fires on the input when the form is submitted with the box unticked, before any submit event
    this.#input.addEventListener('invalid', this.#onInvalid);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('change', this.#onChange);
    this.#input?.removeEventListener('invalid', this.#onInvalid);
  }

  /** @returns {HTMLElement | null} */
  get #hint() {
    return document.getElementById(this.dataset.hint || '');
  }

  /** @param {Event} event */
  #onInvalid = (event) => {
    event.preventDefault();
    const hint = this.#hint;
    if (hint) {
      // the live region is rendered empty; filling it is what screen readers announce
      hint.textContent = hint.dataset.text || '';
      this.#input?.setAttribute('aria-describedby', hint.id);
    }
    this.classList.add('ncc-age-gate--attention');
    this.#input?.focus();
  };

  #onChange = () => {
    if (!this.#input) return;
    if (this.#input.checked) {
      const hint = this.#hint;
      if (hint) hint.textContent = '';
      this.#input.removeAttribute('aria-describedby');
      this.classList.remove('ncc-age-gate--attention');
    }
    this.#save(this.#input.checked);
  };

  /**
   * Writes (or clears) the confirmation on the cart so the state survives re-renders and reaches the order.
   * Saves run one after another so a quick tick, untick, tick ends with the last state on the server.
   * @param {boolean} confirmed
   */
  #save(confirmed) {
    const attributes = {
      [this.dataset.attribute || 'Age confirmation']: confirmed ? this.dataset.value || 'Confirmed 18 or over' : '',
    };
    const url = (typeof Theme !== 'undefined' && Theme.routes?.cart_update_url) || `${window.Shopify?.routes?.root || '/'}cart/update.js`;
    this.#queue = this.#queue
      .then(() =>
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        })
      )
      .then((response) => {
        if (!response.ok) throw new Error(`cart/update.js responded ${response.status}`);
      })
      .catch((error) => console.warn('[ncc-age-gate]', error));
  }
}

if (!customElements.get('ncc-age-gate')) customElements.define('ncc-age-gate', AgeGate);
